/* ==========================================================================
   Contact Page Component (Headquarters, Development Center & Contact Form)
   100% Multi-Language i18n
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { createHumanVerification } from '../components/HumanVerification.js';
import { pushContactFormSuccess } from '../utils/gtmTracker.js';

export function renderContactPage() {
  const container = document.createElement('div');
  container.className = 'sed-page';

  container.innerHTML = `
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem;">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 800px; margin-bottom: 2rem;">
          <div class="sed-eyebrow">${t('contactPage.eyebrow') || 'GET IN TOUCH'}</div>
          <h1 class="sed-page-hero-title">${t('contactPage.title')}</h1>
          <p class="sed-section-desc">
            ${t('contactPage.subtitle')}
          </p>
        </div>
      </div>
    </section>

    <section class="sed-section" style="background-color: #ffffff;">
      <div class="sed-container">
        <div class="sed-hero-grid">
          <div>
            <!-- Location 1: Headquarters -->
            <div class="sed-card" style="margin-bottom: 1.25rem;">
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 0.75rem;">
                <span style="background: var(--blue-ultra-light); color: var(--blue-primary); padding: 4px 10px; border-radius: 6px; font-weight: 800; font-size: 0.76rem; text-transform: uppercase;">${t('contactPage.headquarters')}</span>
              </div>
              <h3 class="sed-card-title">${t('contactPage.hqTitle')}</h3>
              <p class="sed-card-text" style="line-height: 1.7; color: var(--text-dark); font-size: 0.95rem;">
                📍 Nepomucká 261, 326 00 Plzeň, Czech Republic<br>
                📞 Phone: +420 910 128 626<br>
                ✉️ Email: <span class="sed-obf-email" data-u="aW5mbw==" data-d="amFsdWQtZW1iZWRkZWQuY29t" data-t="aW5mb0BqYWx1ZC1lbWJlZGRlZC5jb20="><span class="sed-obf-fallback">info [at] jalud-embedded.com</span></span><br>
                🌐 Web: soundeventdetector.eu · jalud.eu
              </p>
            </div>

            <!-- Location 2: Development -->
            <div class="sed-card" style="margin-bottom: 1.25rem;">
              <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 0.75rem;">
                <span style="background: rgba(16, 185, 129, 0.1); color: #059669; padding: 4px 10px; border-radius: 6px; font-weight: 800; font-size: 0.76rem; text-transform: uppercase;">${t('contactPage.development')}</span>
              </div>
              <h3 class="sed-card-title">${t('contactPage.devTitle')}</h3>
              <p class="sed-card-text" style="line-height: 1.7; color: var(--text-dark); font-size: 0.95rem;">
                📍 Cukrovarská 20, 301 00 Plzeň, Czech Republic<br>
                📞 Office Phone: +420 910 128 626<br>
                💼 ${t('contactPage.businessHours')}
              </p>
            </div>

            <!-- Global Support Info -->
            <div class="sed-card">
              <h3 class="sed-card-title">${t('contactPage.supportTitle')}</h3>
              <p class="sed-card-text" style="line-height: 1.7; font-size: 0.9rem; color: var(--text-muted);">
                📞 Direct Phone: +420 910 128 626<br>
                🔒 ${t('contactPage.isoCert')}<br>
                🇪🇺 ${t('contactPage.euTrademark')}
              </p>
            </div>
          </div>

          <!-- Contact Form -->
          <div class="sed-card" style="position: relative;">
            <h3 class="sed-card-title" style="margin-bottom: 1.25rem;">${t('contactPage.title')}</h3>

            <div id="contact-form-alert" style="display: none; margin-bottom: 1.25rem; padding: 1rem 1.25rem; border-radius: var(--radius-unified); font-size: 0.92rem; line-height: 1.5;"></div>

            <form id="sed-contact-page-form" style="display: flex; flex-direction: column; gap: 1rem;">
              <!-- Honeypot anti-spam field -->
              <input type="text" name="_gotcha" style="display:none !important;" tabindex="-1" autocomplete="off">

              <div class="sed-form-grid-2">
                <div>
                  <label style="font-size: 0.85rem; font-weight: 700; display: block; margin-bottom: 0.3rem;">${t('contactPage.name')} *</label>
                  <input type="text" id="contact-name" name="name" placeholder="John Doe" required style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none;">
                </div>
                <div>
                  <label style="font-size: 0.85rem; font-weight: 700; display: block; margin-bottom: 0.3rem;">${t('contactPage.company')}</label>
                  <input type="text" id="contact-company" name="company" placeholder="Company or City" style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none;">
                </div>
              </div>

              <div class="sed-form-grid-2">
                <div>
                  <label style="font-size: 0.85rem; font-weight: 700; display: block; margin-bottom: 0.3rem;">${t('contactPage.email')} *</label>
                  <input type="email" id="contact-email" name="email" placeholder="john.doe@company.com" required style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none;">
                </div>
                <div>
                  <label style="font-size: 0.85rem; font-weight: 700; display: block; margin-bottom: 0.3rem;">${t('contactPage.phone')}</label>
                  <input type="tel" id="contact-phone" name="phone" placeholder="+420 123 456 789" style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none;">
                </div>
              </div>

              <div>
                <label style="font-size: 0.85rem; font-weight: 700; display: block; margin-bottom: 0.3rem;">${t('contactPage.facility')} *</label>
                <textarea id="contact-message" name="message" rows="4" placeholder="${t('contactPage.placeholder')}" required style="width: 100%; padding: 0.75rem 1rem; border: 1px solid var(--slate-line); border-radius: var(--radius-unified); outline: none; font-family: var(--font-body);"></textarea>
              </div>

              <!-- Human Verification / Anti-Bot Checkbox -->
              <div id="contact-captcha-mount"></div>

              <button type="submit" id="contact-submit-btn" class="sed-btn sed-btn-primary" style="height: 48px; font-size: 1rem; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 8px;">
                <span>${t('contactPage.submit')}</span>
              </button>

              <div style="font-size: 0.78rem; color: var(--text-light); text-align: center;">
                🔒 ${t('contactPage.inquiryNotice')}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  `;

  // Attach Interactive Form Submit Handler & Human Verification
  const form = container.querySelector('#sed-contact-page-form');
  const alertBox = container.querySelector('#contact-form-alert');
  const submitBtn = container.querySelector('#contact-submit-btn');
  const captchaMount = container.querySelector('#contact-captcha-mount');

  const humanVerification = createHumanVerification({
    id: 'contact-page-captcha'
  });
  if (captchaMount) {
    captchaMount.appendChild(humanVerification.element);
  }

  if (form) {
    let isSubmitting = false;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (isSubmitting) return;

      if (!humanVerification.isHumanVerified()) {
        humanVerification.highlightRequired();
        return;
      }

      const verifyData = humanVerification.getPayload();
      const name = form.querySelector('#contact-name')?.value || '';
      const company = form.querySelector('#contact-company')?.value || '';
      const email = form.querySelector('#contact-email')?.value || '';
      const phone = form.querySelector('#contact-phone')?.value || '';
      const message = form.querySelector('#contact-message')?.value || '';

      if (!name.trim() || !email.trim() || !message.trim()) {
        return;
      }

      if (verifyData._gotcha || verifyData.b_website || verifyData.company_fax) {
        form.reset();
        humanVerification.reset();
        return;
      }

      // Set Loading UI State & Re-entrancy Guard
      isSubmitting = true;
      submitBtn.disabled = true;
      submitBtn.innerHTML = `
        <span class="sed-spinner" style="display: inline-block; width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #ffffff; border-radius: 50%; animation: spin 0.8s linear infinite;"></span>
        <span>${t('contactPage.sending')}</span>
      `;
      alertBox.style.display = 'none';

      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            name,
            company,
            email,
            phone,
            message,
            source: 'Contact Page Direct Form',
            verificationToken: verifyData.verificationToken,
            verificationTimestamp: verifyData.verificationTimestamp,
            _gotcha: verifyData._gotcha,
            b_website: verifyData.b_website,
            company_fax: verifyData.company_fax
          })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          pushContactFormSuccess({
            formId: 'sed-contact-page-form',
            formLocation: 'contact_page',
            formLanguage: getLocale() || 'en',
            leadType: 'sed',
            value: 1,
            currency: 'EUR'
          });
          form.reset();
          humanVerification.reset();
          alertBox.style.display = 'block';
          alertBox.style.backgroundColor = 'var(--green-light)';
          alertBox.style.color = 'var(--green-dark)';
          alertBox.style.border = '1px solid var(--green-primary)';
          alertBox.innerHTML = `
            <strong>${t('contactPage.successTitle')}</strong><br>
            ${t('contactPage.successDesc')}
          `;
        } else {
          throw new Error(data.error || 'Submission failed');
        }
      } catch (err) {
        console.error('Contact Form Error:', err);
        alertBox.style.display = 'block';
        alertBox.style.backgroundColor = '#fef2f2';
        alertBox.style.color = '#991b1b';
        alertBox.style.border = '1px solid #ef4444';
        alertBox.innerHTML = `
          <strong>${t('contactPage.errorTitle')}</strong><br>
          ${t('contactPage.errorDesc')}
        `;
      } finally {
        isSubmitting = false;
        submitBtn.disabled = false;
        submitBtn.innerHTML = `<span>${t('contactPage.submit')}</span>`;
      }
    });
  }

  return container;
}
